import React from 'react'
import Image from 'next/image'

import PopupSignUp from '@/components/blocks/PopupSignUp'
import { Column, Layout, Row, Rows, Section } from '@/components/layout'
import { Text } from '@/components/common'
import IconInfoBlock from '@/components/blocks/IconInfoBlock'
import TrackReceiptsHeader from '@/components/sections/TrackReceiptsHeader'
import LogoTryFreeNav from '@/components/sections/LogoTryFreeNav'
import itemizationImg from '@/assets/track-receipts/itemization.png'
import trendsInsightsImg from '@/assets/track-receipts/trends-insights.png'
import fileTaxesImg from '@/assets/integrations/mapping-to-quickbooks.jpg'
import matchingReconciliationImg from '@/assets/transaction-streaming/matching-reconciliation.png'
import MobileAppFeatures from '@/components/blocks/MobileAppFeatures'
import TestimonialSliderSlim from '@/components/blocks/TestimonialSliderSlim'
import { ORDER_SOURCE } from '@/constants'

import styles from './track-receipts.module.scss'

const pageId = 'trackReceiptsTrial'

const testimonials = [
  {
    text: 'It makes budgeting, tax preparation and documentation on my spending so easy. I have been a neat receipts user for over ten years and only once had a problem that the service dept. worked on quickly and efficiently.',
    author: 'Patricia M.',
  },
  {
    text: 'I am so thrilled learning more about NEAT. I\'ve used it for years and never knew it was such an Organizational BEAST! LOVE IT!',
    author: 'Douglas G.',
  },
  {
    text: 'Awesome to keep track of receipts.',
    author: 'Brian C.',
  },
  {
    text: 'Neat has cut my tax preparation from literally weeks, down to two days. Love it.',
    author: 'James F.',
  },
  {
    text: 'I have been using Neat for at least 7 years, and it\'s made tracking receipts and generating year end reports a breeze.',
    author: 'Joann D.',
  },
  {
    text: 'Makes end of the year filing so easy! No need to save receipts.',
    author: 'Michael K.',
  },
  {
    text: 'I meet with clients to do home closings every day. I tell them the benefits of using Neat.com. Most homeowners never think they may have a fire or flood. I have to remind them that paper or backup drives will be destroyed during both tragedies.',
    author: 'LaTonja A.',
  },
  {
    text: 'It pulls the information off the page, actually reads the text that\'s on there, and organizes it for you.',
    author: 'Josh Z.',
  },
  {
    text: 'The system is fantastic and eliminates many hours of filing and finding files.',
    author: 'Andy K.',
  },
]

const appFeatures = [
  {
    description: 'Receipt capture & import',
  },
  {
    description: 'Access, organize & share receipts & docs',
  },
  {
    description: 'Find receipts instantly with powerful search',
  },
  {
    description: 'Manage documents, transactions, and expenses',
  },
  {
    description: 'Connect financial accounts',
  },
]

export default function TrackReceiptsTrial() {
  return (
    <Layout.Content className={styles.trackReceipts}>
      <LogoTryFreeNav customMessageId="cta.start14DayTrial" fixedNav />

      <TrackReceiptsHeader
        highlightedText="Start scanning receipts today from your mobile phone, compatible scanner, email or drag and drop. Know how Neat will work for you with our FREE 14-day trial!"
        subHeadline="Scan, store, and categorize receipts instantly with Neat’s powerful receipt tracker software. Simplify your budget and taxes with one simple app."
        tryOnly
      />

      <Section className={styles.receiptManagement} paddingBottom={24}>
        <Rows gap={0}>
          <Row center textCenter>
            <h2 className="c-brand">Receipt scanning & management from anywhere</h2>
          </Row>
          <div className={styles.receiptManagementGrid}>
            <IconInfoBlock img="/track-receipts/icons/searchable.svg" title="Scan Receipts, Track Everywhere" wide>
              <p>Snap a picture using our mobile app, email, drag and drop documents into Neat, or import with a compatible scanner. Then, add these items to expense reports for quick review/analysis or timely reimbursement.</p>
            </IconInfoBlock>

            <IconInfoBlock img="/track-receipts/icons/data-extraction.svg" title="Save Time On Data Entry" wide>
              <p>Our powerful OCR software can extract information from any paper document, allowing you to enter receipts quickly and accurately.</p>
            </IconInfoBlock>
            <IconInfoBlock img="/track-receipts/icons/always-organized.svg" title="Organize Files Your Way" wide>
              <p>Organize files by expense type, tax category, and more. Quickly find files with our keyword search and filtering capabilities.</p>
            </IconInfoBlock>

            <IconInfoBlock img="/track-receipts/icons/integrations.png" title="Connect With Integrations" wide>
              <p>Send your data to QuickBooks, TurboTax, H&amp;R Block, and more with a click of a button.</p>
            </IconInfoBlock>
          </div>

        </Rows>
      </Section>

      <MobileAppFeatures appFeatures={appFeatures} />

      <Section paddingTop={0}>
        <Rows gap={64}>
          <Row center>
            <h2 className="text-center">Enhance Your Experience with Automated Insights</h2>
          </Row>
          <Row>
            <Column alignCenter textCenter>
              <Image
                alt=""
                src={itemizationImg}
                style={{ maxHeight: '300px', width: 'auto' }}
              />
              <h3>Organize Data with Line Itemization</h3>
              <p>Breakdown your receipts, line-by-line. You can categorize, split, add, and edit line items for any matched receipt. Say goodbye to manually importing and managing multiple copies of the same receipt.</p>
            </Column>
            <Column alignCenter textCenter>
              <Image
                alt=""
                src={trendsInsightsImg}
                style={{ maxHeight: '300px', width: 'auto' }}
              />
              <h3>View Trends and Insights</h3>
              <p>Easily monitor and analyze your finances with live data about your cash balance, top expense categories, total sales, and more. All automated through your stream of transactions!</p>
            </Column>
          </Row>
          <Row>
            <Column alignCenter textCenter>
              <Image
                alt=""
                src={fileTaxesImg}
                style={{ maxHeight: '300px', width: 'auto' }}
              />
              <h3>File Your Taxes Quickly (And Accurately)</h3>
              <p>Easily send expenses to your tax or accounting software. Map expenses a single time to match your QuickBooks preferences, and incoming data will be automatically coded. Export tax categories, expenses, and document images to tax software like TurboTax to eliminate the need to type in data.</p>
            </Column>
            <Column alignCenter textCenter>
              <Image
                alt=""
                src={matchingReconciliationImg}
                style={{ maxHeight: '300px', width: 'auto' }}
              />
              <h3>Reconcile Receipts and Invoices</h3>
              <p>Match, categorize and reconcile with ease. Have confidence that all of your transactions are centralized and accurate. No need to reconcile off of a bank statement, every transaction source is accounted for in Neat.</p>
            </Column>
          </Row>
        </Rows>
      </Section>

      <Section bgSecondary>
        <Text.H2 style={{ marginBottom: '30px' }}>Customers love Neat!</Text.H2>
        <Column wrap>
          <TestimonialSliderSlim fixedHeight="250" lightTheme multiSlides slides={testimonials} />
        </Column>
      </Section>

      <PopupSignUp exitIntent source={ORDER_SOURCE.TRACK_RECEIPTS_TRIAL} />
    </Layout.Content>
  )
}

TrackReceiptsTrial.layoutProps = {
  pageId,
  showHeader: false,
  noNavFooter: true,
  shouldRobotsIndex: false,
}
